/* EditorLayoutComponent.js */

"use strict";
const LayoutManager = require("@gluenetworks/layout_manager");
const Renderer = require("./EditorLayoutRenderer");
const _ = require("lodash");
const TabComponent = require("./editortab/EditorTabComponent");

class EditorLayoutComponent extends LayoutManager {
    constructor(di, logger) {
        super();
        this._di = di.createDependencyInjector(); //<--- Managed by the parent LayoutManager Class
        this._logger = logger;

        this._registeredEditors = {};
        this._launchedEditors = {};

        this._registerListeners();
        this._renderer = new Renderer(this);
        super.setNamespaceGlobs("**");
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        let proms = [];
        this._unregisterListeners();
        proms.push(this._renderer.shutdown());
        proms.push(super.shutdown());
        let allProms = Promise.all(proms);
        //equivalent to a finally
        allProms
            .then(() => {})
            .catch(() => {})
            .then(() => {
                this._di = undefined;
                this._logger = undefined;
                this._renderer = undefined;
                this._launchedEditors = undefined;
                this._registeredEditors = undefined;
            });
        return allProms;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _createEditorFactory() {
        let that = this;
        return function (goldenLayoutContainer, componentState) {
            let injectableOverrides = {
                "editorLayout.editorContainer": goldenLayoutContainer,
                "editorLayout.editorState": componentState,
                "editorLayout.editorLayoutMgr": that,
            };
            let editorName = _.get(componentState, "editorName");
            let registeredItem = that.getItem(editorName);
            let itm = registeredItem.registeredObj;
            let editorTab;

            //create Tab Component
            if (_.get(itm, "$editorTabComponentClass")) {
                editorTab = itm.$editorTabComponentClass;
            } else {
                editorTab = new TabComponent();
            }
            //Add the tab component to the injector
            injectableOverrides["editorLayout.editor.tabComponent"] = editorTab;

            let createResults = that.createScopedItem(editorName, {
                defaultInjectables: [
                    "$teve.logger",
                    "$teve.registeredDetails",
                    "editorLayout.editorContainer",
                    "editorLayout.editorState",
                    "editorLayout.editorLayoutMgr",
                    "editorLayout.editor.tabComponent",
                ],
                injectableOverrides: injectableOverrides,
            });
            let editorInstance = createResults.item;
            let editorDI = createResults.scopedDependencyInjector;
            //store the editorDI using the editorsId
            that._addRegisteredEditor(componentState.id, editorDI);

            let onTabHdlr = (tab) => {
                let tabElem = tab.element[0];

                goldenLayoutContainer.tabElement = tab;
                //setup tab to focus on click
                tabElem.dataset.editorId = componentState.id;
                tabElem.addEventListener("click", () => {
                    goldenLayoutContainer.layoutManager.focusEditor(componentState.id);
                });

                //Try and pull the title from the launchOpts if its not there try pulling it from the state fall back to the editor Name
                let titleText = _.get(
                    componentState,
                    "launchOptions.title",
                    _.get(componentState, "title", editorName)
                );
                let titleTemplate = _.template(titleText);
                let compiledTitleText = titleTemplate(componentState);
                if (!editorTab.title) {
                    editorTab.setTitle(compiledTitleText, true);
                }
                tabElem.removeAttribute("title");

                //if icon is specified and is not initialized already
                if (!editorTab.editorIconName && _.get(itm, "$editorIcon")) {
                    editorTab.setEditorIconName(itm.$editorIcon, true);
                }
                //if icon type is specified
                if (_.get(itm, "$editorIconType")) {
                    editorTab.setEditorIconType(itm.$editorIconType, true);
                }
                //if action icon is specified
                if (_.get(itm, "$editorTabAction")) {
                    editorTab.setEditorAction(itm.$editorTabAction, true);
                    editorTab.on("EditorTabActionIconClicked", (data) => {
                        editorInstance.emit("EditorTabActionIconClicked", data);
                    });
                }

                //Remove default Tab Title
                let defaultTabTitle = tabElem.querySelector(".lm_title");
                defaultTabTitle.remove();

                //Insert the editor Tab as the first child element
                tabElem.insertBefore(
                    editorTab.renderer.rootElement,
                    tabElem.firstChild.nextSibling
                );
                editorTab.renderer.render();

                //If advanced tab interaction is required
                if (_.isFunction(editorInstance.tabInitialized)) {
                    editorInstance.tabInitialized(editorTab);
                }
            };
            goldenLayoutContainer.on("tab", onTabHdlr);
            goldenLayoutContainer.on("destroy", () => {
                try {
                    goldenLayoutContainer.off("tab", onTabHdlr);
                } catch (e) {
                    //this._logger.info("tab listeners already cleaned up");
                }
            });
            goldenLayoutContainer.getElement()[0].appendChild(editorInstance.renderer.rootElement);
            editorInstance.renderer.render();
            return editorInstance;
        };
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START LAUNCHED EDITORS METHODS */
    /*******************************************/
    get launchedEditors() {
        return this.getLaunchedEditors();
    }
    getLaunchedEditors() {
        return _.values(this._launchedEditors);
    }
    getLaunchedEditorsAsMap() {
        return this._launchedEditors;
    }
    getLaunchedEditor(id) {
        return _.get(this._launchedEditors, id);
    }
    _removeLaunchedEditor(id) {
        if (_.has(this._launchedEditors, id)) {
            let removed = this._launchedEditors[id];
            delete this._launchedEditors[id];
            return removed;
        }
    }
    _insertLaunchedEditor(launchResults) {
        let launchedEditor = {
            id: launchResults.editorId,
            component: launchResults.editorComponent,
            state: launchResults.editorState,
            name: launchResults.editorName,
            launchOptions: launchResults.launchOptions,
            instance: launchResults.editorComponent.instance,
        };
        _.set(this._launchedEditors, launchResults.editorId, launchedEditor);
    }
    _addRegisteredEditor(editorId, scopedCreationResults) {
        let alredyRegistered = this._getRegisteredEditor(editorId);
        if (alredyRegistered) {
            this._logger.warn(
                {
                    editorId: editorId,
                    currentRegistration: alredyRegistered,
                    newRegistration: scopedCreationResults,
                },
                "Registering an Editor that has already been registered, this is weird..."
            );
        }
        this._registeredEditors[editorId] = scopedCreationResults;
    }
    _getRegisteredEditor(editorId) {
        return this._registeredEditors[editorId] || null;
    }
    _unRegisterEditor(editorId) {
        if (_.has(this._registeredEditors, editorId)) {
            let removed = this._registeredEditors[editorId];
            delete this._registeredEditors[editorId];
            return removed;
        }
    }

    /*******************************************/
    /* END LAUNCHED EDITORS METHODS */
    /* START PUBLIC API ACTION METHODS */
    /*******************************************/
    resizeContainer(sizes) {
        this.emit("ResizeContainer", sizes);
    }
    launchEditor(editorState, editorName, launchOpts) {
        let registeredItem = super.getItem(editorName);
        if (!registeredItem) {
            return Promise.reject({
                errorCode: "EDITOR_NOT_FOUND",
                errorMsg: `There is currently no editors registered via the fully qualified name (${editorName})`,
            });
        } else {
            let editorStartDetails = {
                editorName: editorName,
                editorState: editorState,
                launchOptions: launchOpts,
            };
            let launchEditorPromise = this._renderer.launchEditor(editorStartDetails);
            launchEditorPromise.then((results) => {
                this._insertLaunchedEditor(results);
            });
            return launchEditorPromise;
        }
    }
    focusEditor(editorId) {
        this.emit("FocusEditor", editorId);
    }
    toggleFullscreen(editorId) {
        this.emit("ToggleFullscreen", editorId);
    }
    closeEditor(editorId) {
        this.emit("CloseEditor", editorId);
    }
    closeAllEditors() {
        this.getLaunchedEditors().map((editor) => {
            this.closeEditor(editor.id);
        });
    }
    editorClosed(editorId) {
        let removedEditorParams = this._removeLaunchedEditor(editorId);
        if (removedEditorParams) {
            //lookup/remove the Dep Injector by editorId
            let editorDi = this._unRegisterEditor(editorId);
            //shutdown the Dep Injector
            if (editorDi) {
                editorDi.shutdown();
            }
            this.emit("EditorClosed", editorId, removedEditorParams);
        }
    }

    activeTabChanged(newTabItem) {
        if (newTabItem && newTabItem.config && newTabItem.config.id) {
            const id = newTabItem.config.id,
                editorParams = this._launchedEditors[id];
            if (editorParams) {
                this.emit("ActiveTabChanged", id, editorParams);
            } else {
                this.emit("ActiveTabOpened", id);
            }
        }
    }

    reinitializeEditorLayoutState(state) {
        return this._renderer.reinitializeEditorLayoutState(state);
    }
    getCurrentEditorLayoutState() {
        return this._renderer.getCurrentEditorLayoutState();
    }

    /*******************************************/
    /* END PUBLIC API ACTION METHODS */
    /*******************************************/
}
module.exports = EditorLayoutComponent;
EditorLayoutComponent.$inject = ["$teve.di", "$teve.logger"];
